import { useMemo } from 'react'
import { Banknote, Dog, Receipt, Users, type LucideIcon } from 'lucide-react'
import type { Booking, Database, Invoice } from '../data/model'
import { useStore } from '../data/store'
import { todayISO } from '../lib/dates'
import { cx } from '../ui/kit'
import type { TKey } from '../i18n'

/**
 * Der Zustand einer Buchung, direkt auf dem Kalenderbalken.
 *
 * WARUM ES DAS GIBT: der Vermieter hat den Hörer am Ohr und den Kalender vor sich.
 * „Ist bei Huber die Anzahlung da?" darf nicht bedeuten, dass er die Buchung öffnet,
 * liest, schließt und den Faden verliert. Der Balken ist der Arbeitsplatz.
 *
 * WARUM SO WENIG: der Balken ist auf dem Handy oft 44 Pixel breit — eine Nacht.
 * Darauf passen entweder drei Buchstaben des Namens oder ein Symbol. Sechs bunte
 * Punkte auf so einem Balken sind schlechter als keiner: dann steht dort ein
 * Farbcode, den niemand auswendig kann, und der Name, an dem der Vermieter die
 * Buchung überhaupt erkennt, ist weg. Deshalb zwei Sparregeln:
 *
 * 1. Es erscheint nur, was eine Handlung verlangt. Eine bezahlte Rechnung bekommt
 *    kein Symbol — „alles in Ordnung" ist die Abwesenheit von Symbolen, und das ist
 *    der Zustand der meisten Buchungen. Ein Symbol auf dem Balken heißt damit immer:
 *    hier ist etwas zu tun.
 * 2. Was nicht mehr passt, fällt weg — in dieser Reihenfolge, von wichtig nach
 *    verzichtbar (siehe `markersFor`). Der Name behält immer Vorrang vor dem letzten
 *    Symbol.
 *
 * Was wegfällt, ist nicht verloren: der `title` des Balkens trägt jeden Zustand als
 * Klartext, auch den unauffälligen („Anzahlung eingegangen"). Das ist zugleich die
 * barrierefreie Fassung — Farbe allein sagt nichts, und ein 6-Pixel-Punkt ist auf
 * keinem Handy zu treffen, deshalb hängt der Tooltip am ganzen Balken.
 */

export type PrepayState = 'not_required' | 'open' | 'received'
export type InvoiceState = 'none' | 'draft' | 'open' | 'overdue' | 'paid' | 'cancelled'

export interface BookingSignals {
  prepay: PrepayState
  invoice: InvoiceState
  /**
   * Der Aufenthalt ist vorbei und war eine echte Buchung. Erst dann ist eine
   * fehlende Rechnung ein Versäumnis und keine Selbstverständlichkeit — vor der
   * Anreise hat schlicht noch niemand eine geschrieben.
   */
  billingDue: boolean
  pet: boolean
  adults: number
  children: number
}

/** Auf zwei Nachkommastellen — Geld, das auf Cent verglichen wird. */
const round2 = (n: number) => Math.round(n * 100) / 100
/** Toleranz für „ist ausgeglichen": halbe Cent-Rundungen aus der Preisrechnung. */
const ZERO = 0.004

/** Status, in denen ein Aufenthalt wirklich stattgefunden hat und abzurechnen ist. */
const BILLABLE_STATUSES: Booking['status'][] = ['confirmed', 'checked_in', 'checked_out']

/**
 * Alles, was für einen Balken gebraucht wird — einmal je Kalenderdurchgang gebaut.
 *
 * WARUM ein Index und nicht `store.paidFor()` je Balken: der Kalender zeichnet über
 * hundert Buchungen und baut beim Scrollen ständig neu. Jede Buchung durch alle
 * Zahlungen und Rechnungen laufen zu lassen ist ein Produkt, kein Aufwand — bei einem
 * Betrieb mit ein paar Jahren Historie ruckelt genau davon das Scrollen.
 */
export interface SignalIndex {
  prepaidBy: Map<string, number>
  paidBy: Map<string, number>
  invoicesBy: Map<string, Invoice[]>
  petFeeIds: Set<string>
  today: string
}

/**
 * Erkennt die Haustiergebühr am Code, nicht am Namen: der Name ist übersetzt und vom
 * Vermieter frei editierbar („Bello"), der Code ist die Kennung aus den Stammdaten.
 */
function isPetFee(code: string): boolean {
  const c = code.toLowerCase()
  return c === 'pet' || c.startsWith('pet_')
    || c.includes('dog') || c.includes('hund') || c.includes('haustier')
}

export function buildSignalIndex(
  db: Pick<Database, 'payments' | 'invoices' | 'fees'>, today: string,
): SignalIndex {
  const prepaidBy = new Map<string, number>()
  const paidBy = new Map<string, number>()
  for (const p of db.payments) {
    if (!p.booking_id) continue
    if (p.kind === 'prepayment') {
      prepaidBy.set(p.booking_id, (prepaidBy.get(p.booking_id) ?? 0) + p.amount)
    }
    // Spiegelt `store.paidFor()`: die Kaution ist geliehenes Geld und tilgt keine
    // Rechnung. Weicht die Regel dort ab, muss sie hier mitgehen.
    if (p.kind !== 'security_deposit' && p.kind !== 'security_deposit_refund') {
      paidBy.set(p.booking_id, (paidBy.get(p.booking_id) ?? 0) + p.amount)
    }
  }

  const invoicesBy = new Map<string, Invoice[]>()
  for (const inv of db.invoices) {
    if (!inv.booking_id) continue
    const list = invoicesBy.get(inv.booking_id)
    if (list) list.push(inv)
    else invoicesBy.set(inv.booking_id, [inv])
  }

  const petFeeIds = new Set(db.fees.filter((f) => isPetFee(f.code)).map((f) => f.id))
  return { prepaidBy, paidBy, invoicesBy, petFeeIds, today }
}

/** Sperren haben keinen Gast, keine Anzahlung und keine Rechnung: `null`. */
export function signalsFor(b: Booking, idx: SignalIndex): BookingSignals | null {
  if (b.kind === 'block') return null

  const wanted = round2(b.prepayment_amount)
  const received = round2(idx.prepaidBy.get(b.id) ?? 0)
  const prepay: PrepayState = wanted <= ZERO
    ? 'not_required'
    : received >= wanted - ZERO
      ? 'received'
      // Der Stempel auf der Buchung ist nur die Folge der Zahlung, nicht die Wahrheit
      // selbst. Steht er aber ohne Zahlungszeile da — Altbestand, Stripe-Abgleich —,
      // dann wäre Mahnen falsch: das Geld ist da, nur die Zeile fehlt.
      : (b.prepaid_at && received <= ZERO) ? 'received' : 'open'

  const invoices = idx.invoicesBy.get(b.id) ?? []
  // Gutschriften zählen nicht als eigene Forderung: sie kehren eine Rechnung um und
  // tragen deren Betrag negativ. Wer sie mitsummiert, bekommt für jeden Storno eine
  // Buchung mit „minus 800 € offen".
  const issued = invoices.filter((i) => i.issued_at && i.type !== 'credit_note')
  const active = issued.filter((i) => i.status !== 'cancelled')
  let invoice: InvoiceState
  if (issued.length === 0) invoice = invoices.length > 0 ? 'draft' : 'none'
  else if (active.length === 0) invoice = 'cancelled'
  else {
    const open = round2(
      active.reduce((sum, i) => sum + i.total_gross, 0) - (idx.paidBy.get(b.id) ?? 0))
    invoice = open <= ZERO
      ? 'paid'
      : active.some((i) => i.status === 'overdue' || (i.due_date && i.due_date < idx.today))
        ? 'overdue'
        : 'open'
  }

  return {
    prepay,
    invoice,
    billingDue: b.departure <= idx.today && BILLABLE_STATUSES.includes(b.status),
    pet: b.optional_fee_ids.some((id) => idx.petFeeIds.has(id)),
    adults: b.adults,
    children: b.children,
  }
}

/** Baut den Index aus dem Store und gibt die Auswertung je Buchung zurück. */
export function useBookingSignals(): (b: Booking) => BookingSignals | null {
  const store = useStore()
  const { payments, invoices, fees } = store.db
  const idx = useMemo(
    () => buildSignalIndex({ payments, invoices, fees }, todayISO()),
    [payments, invoices, fees])
  return useMemo(() => (b: Booking) => signalsFor(b, idx), [idx])
}

/* ------------------------------------------------------------------ Darstellung */

/**
 * Schwellen in Pixel Balkenbreite.
 *
 * Ein Zeichen dieser Schrift ist gut 6 px breit, ein Symbolplättchen 16 px plus 3 px
 * Abstand, die Innenabstände des Balkens kosten 14 px. Unter 68 px — auf dem Handy
 * eine Nacht, auf dem Schreibtisch auch — bliebe neben einem Symbol nicht einmal ein
 * halber Vorname stehen, deshalb dort gar keines: lieber ein Balken, den man am Namen
 * erkennt und antippt, als ein anonymes Ampelfeld.
 */
const W_ONE_MARKER = 68
const W_TWO_MARKERS = 112
const W_THREE_MARKERS = 168
/** Ab dieser Breite lohnt die Belegungszeile — darunter frisst sie den Namen. */
const W_OCCUPANCY = 56
/**
 * Ab dieser Balkenhöhe passt eine zweite Zeile unter den Namen (rowH minus 12 px
 * Rand). Wer die Zeilen auf Minimum zieht, will den Monat sehen und nicht die Details;
 * bei der Standardhöhe von 52 px ist die Belegung da.
 */
const H_OCCUPANCY = 38

export type MarkerTone = 'danger' | 'warn' | 'neutral'

export interface BarMarker {
  key: 'invoice' | 'prepay' | 'pet'
  tone: MarkerTone
  icon: LucideIcon
  labelKey: TKey
}

/**
 * Welche Symbole ein Balken dieser Breite verträgt — in der Reihenfolge, in der sie
 * gebraucht werden.
 *
 * Zuerst Geld, das schon zu spät ist, dann Geld, das noch kommen soll, dann die
 * fehlende Rechnung nach der Abreise. Der Hund kommt zuletzt: er ist eine Information
 * für die Reinigung und die nächste Belegung, aber niemand muss deswegen heute
 * telefonieren.
 */
export function markersFor(s: BookingSignals, width: number): BarMarker[] {
  const budget = width >= W_THREE_MARKERS ? 3
    : width >= W_TWO_MARKERS ? 2
      : width >= W_ONE_MARKER ? 1 : 0
  if (budget === 0) return []

  const all: BarMarker[] = []
  if (s.invoice === 'overdue') {
    all.push({ key: 'invoice', tone: 'danger', icon: Receipt, labelKey: 'calendar.bar.invoiceOverdue' })
  }
  if (s.prepay === 'open') {
    all.push({ key: 'prepay', tone: 'warn', icon: Banknote, labelKey: 'calendar.bar.prepayOpen' })
  }
  if (s.invoice === 'open') {
    all.push({ key: 'invoice', tone: 'warn', icon: Receipt, labelKey: 'calendar.bar.invoiceOpen' })
  }
  if (s.billingDue && (s.invoice === 'none' || s.invoice === 'draft' || s.invoice === 'cancelled')) {
    all.push({ key: 'invoice', tone: 'warn', icon: Receipt, labelKey: 'calendar.bar.invoiceMissing' })
  }
  if (s.pet) {
    all.push({ key: 'pet', tone: 'neutral', icon: Dog, labelKey: 'calendar.bar.pet' })
  }
  return all.slice(0, budget)
}

/**
 * Die Legende — abgeleitet aus derselben Liste, aus der die Balken ihre Symbole
 * beziehen, nicht daneben gepflegt.
 *
 * Eine handgeschriebene Legende ist eine zweite Wahrheit: sie stimmt am Tag ihrer
 * Entstehung und danach nie wieder. Kommt ein Symbol dazu oder ändert eines seine
 * Farbe, muss die Erklärung mitwandern, ohne dass jemand daran denkt.
 *
 * Deshalb wird sie hier aus konstruierten Beispielzuständen erzeugt: für jeden Fall
 * ein Signalsatz, durch `markersFor` geschickt, und heraus fällt genau das Symbol,
 * das der Vermieter auf dem Balken sieht.
 */
export function legendMarkers(): BarMarker[] {
  const base: BookingSignals = {
    prepay: 'not_required', invoice: 'none', billingDue: false,
    pet: false, adults: 0, children: 0,
  }
  // Breite großzügig, damit `markersFor` nichts wegkürzt — die Legende erklärt alle
  // Symbole, auch die, für die auf einem schmalen Balken kein Platz wäre.
  const wide = 9999
  const cases: BookingSignals[] = [
    { ...base, invoice: 'overdue' },
    { ...base, prepay: 'open' },
    { ...base, invoice: 'open' },
    { ...base, billingDue: true },
    { ...base, pet: true },
  ]
  // Entdoppelt wird über die BESCHRIFTUNG, nicht über Symbol und Farbe. Zwei Fälle
  // teilen sich das gelbe Belegzeichen — „Rechnung gestellt, noch offen" und
  // „Aufenthalt vorbei, noch keine Rechnung". Wer nach Symbol entdoppelt, wirft den
  // zweiten weg und erklärt damit ausgerechnet den Fall nicht, den niemand errät.
  // Dass dasselbe Zeichen zweimal in der Liste steht, ist keine Doppelung: es bedeutet
  // wirklich zweierlei.
  const seen = new Set<string>()
  const out: BarMarker[] = []
  for (const c of cases) {
    for (const m of markersFor(c, wide)) {
      if (seen.has(m.labelKey)) continue
      seen.add(m.labelKey)
      out.push(m)
    }
  }
  return out
}

const TONE_CLASS: Record<MarkerTone, string> = {
  danger: 'text-danger',
  warn: 'text-warn',
  neutral: 'text-muted',
}

type T = (key: TKey, vars?: Record<string, string | number>) => string

/** „2 Erwachsene, 1 Kind" — mit Einzahl, weil „1 Kinder" nach Maschine klingt. */
export function occupancyText(s: BookingSignals, t: T): string {
  const parts = [s.adults === 1 ? t('calendar.bar.adult') : t('calendar.bar.adults', { n: s.adults })]
  if (s.children > 0) {
    parts.push(s.children === 1 ? t('calendar.bar.child') : t('calendar.bar.children', { n: s.children }))
  }
  return parts.join(', ')
}

/**
 * Der Klartext zum Balken, eine Zeile je Sachverhalt — auch für die Zustände, die
 * kein Symbol bekommen. Wer nachsieht, will wissen, dass die Anzahlung DA ist, nicht
 * nur, dass nichts blinkt.
 */
export function signalTitleLines(s: BookingSignals, t: T): string[] {
  const prepay: Record<PrepayState, TKey> = {
    not_required: 'calendar.bar.prepayNone',
    open: 'calendar.bar.prepayOpen',
    received: 'calendar.bar.prepayReceived',
  }
  const invoice: Record<InvoiceState, TKey> = {
    none: s.billingDue ? 'calendar.bar.invoiceMissing' : 'calendar.bar.invoiceNone',
    draft: 'calendar.bar.invoiceDraft',
    open: 'calendar.bar.invoiceOpen',
    overdue: 'calendar.bar.invoiceOverdue',
    paid: 'calendar.bar.invoicePaid',
    cancelled: 'calendar.bar.invoiceCancelled',
  }
  const lines = [occupancyText(s, t), t(prepay[s.prepay]), t(invoice[s.invoice])]
  if (s.pet) lines.push(t('calendar.bar.pet'))
  return lines
}

/**
 * Der Inhalt eines Buchungsbalkens: Name, darunter die Belegung, rechts die Symbole.
 *
 * Die Symbole sitzen in einem eigenen, nicht schrumpfenden Kasten, damit der Name an
 * ihnen abgeschnitten wird statt unter sie zu rutschen. `pointer-events-none` ist
 * Pflicht: der Balken selbst ist der Griff zum Verschieben, und ein Symbol, das den
 * Zeiger abfängt, macht genau die Buchungen unanfassbar, die Aufmerksamkeit brauchen.
 * Der Tooltip hängt deshalb am Balken und nicht an den Plättchen.
 */
export function BookingBarContent({ label, signals, width, height }: {
  label: string
  signals: BookingSignals | null
  width: number
  height: number
}) {
  const markers = signals ? markersFor(signals, width) : []
  const showOccupancy = signals !== null
    && height >= H_OCCUPANCY && width >= W_OCCUPANCY
    && signals.adults + signals.children > 0

  return (
    <>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate">{label}</span>
        {/* Voll deckend statt 80 %: die Zeile steht auf einer kräftigen
            Einheitenfarbe, und dort frisst jede Transparenz Kontrast. */}
        {showOccupancy && (
          <span className="flex items-center gap-1 text-[11px] font-medium tabular-nums">
            <Users size={11} strokeWidth={2.5} className="shrink-0" aria-hidden />
            {signals.adults}{signals.children > 0 && `+${signals.children}`}
          </span>
        )}
      </span>

      {markers.length > 0 && (
        <span className="flex items-center gap-0.5 shrink-0 pointer-events-none">
          {markers.map((m) => (
            <span
              key={`${m.key}-${m.labelKey}`}
              // Plättchen unter dem Symbol: der Balken trägt die Farbe der Einheit, und
              // ein bernsteinfarbenes Zeichen darauf ist mal deutlich und mal Matsch.
              // So bleibt der Kontrast unabhängig von der gewählten Farbe.
              className={cx(
                // 18 statt 16 Pixel, Symbol 12 statt 10: bei 16 lag das Zeichen am
                // unteren Rand des Lesbaren, und auf einem farbigen Balken erkennt man
                // dann nur noch, DASS etwas da ist, nicht WAS. Zwei Pixel mehr kosten
                // nichts — der Balken bricht erst deutlich später um (siehe die
                // Schwellen oben), und die Reihenfolge sorgt dafür, dass bei Enge das
                // Unwichtigste zuerst entfällt.
                'w-[18px] h-[18px] rounded-full bg-surface ring-1 ring-black/15 shadow-sm',
                'flex items-center justify-center',
                TONE_CLASS[m.tone],
              )}
              // Kein eigener Tooltip: an einem 16-Pixel-Punkt zielt niemand vorbei am
              // Ziehen der Buchung, und der Balken sagt ohnehin alles im Klartext.
              aria-hidden
            >
              <m.icon size={12} strokeWidth={2.5} />
            </span>
          ))}
        </span>
      )}
    </>
  )
}

/**
 * Ein einzelnes Symbol so, wie es auf dem Balken aussieht — für die Legende.
 *
 * Bewusst dieselben Maße und dieselbe Farblogik wie im Balken. Eine Legende, die ihre
 * Symbole größer oder in anderer Farbe zeigt als das, was danebensteht, erklärt nichts;
 * sie zwingt den Betrachter zu einem Vergleich, den er nicht anstellen sollte.
 */
export function LegendPlate({ marker }: { marker: BarMarker }) {
  return (
    <span
      className={cx(
        'w-[18px] h-[18px] rounded-full bg-surface ring-1 ring-black/15 shadow-sm',
        'flex items-center justify-center shrink-0',
        TONE_CLASS[marker.tone],
      )}
      aria-hidden
    >
      <marker.icon size={12} strokeWidth={2.5} />
    </span>
  )
}
